import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, X, Check } from 'lucide-react';

/**
 * Form section for recording or editing a single sale entry.
 * Extracted from SalesSection.js to keep that file under the 500-LOC limit.
 */
export default function SalesSectionForm({
  form,
  setForm,
  fullSaleOnly,
  editingId,
  saving,
  defaultCurrency,
  onCancel,
  onSave,
}) {
  return (
    <div className="rounded-md border border-slate-300 bg-white p-3 space-y-3" data-testid="sale-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Sale date</Label>
          <Input
            type="date" value={form.sale_date}
            onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
            className="mt-1 h-9 text-sm"
            data-testid="sale-form-sale-date"
          />
        </div>
        {!fullSaleOnly && (
          <div>
            <Label className="text-xs text-slate-600">Mode</Label>
            <div className="mt-1 inline-flex border border-slate-300 rounded-md overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setForm({ ...form, mode: 'fraction' })}
                className={`px-3 py-1.5 ${form.mode === 'fraction' ? 'bg-emerald-700 text-white' : 'bg-white text-slate-700'}`}
                data-testid="sale-mode-fraction"
              >Fraction</button>
              <button
                type="button"
                onClick={() => setForm({ ...form, mode: 'units' })}
                className={`px-3 py-1.5 ${form.mode === 'units' ? 'bg-emerald-700 text-white' : 'bg-white text-slate-700'}`}
                data-testid="sale-mode-units"
              >Units</button>
            </div>
          </div>
        )}
        {!fullSaleOnly && form.mode === 'fraction' && (
          <div>
            <Label className="text-xs text-slate-600">Fraction sold (0–1)</Label>
            <Input
              type="number" step="0.0001" min="0" max="1" placeholder="0.25"
              value={form.fraction_sold}
              onChange={(e) => setForm({ ...form, fraction_sold: e.target.value })}
              className="mt-1 h-9 text-sm"
              data-testid="sale-form-fraction"
            />
          </div>
        )}
        {!fullSaleOnly && form.mode === 'units' && (
          <>
            <div>
              <Label className="text-xs text-slate-600">Units sold</Label>
              <Input
                type="number" step="0.0001" min="0"
                value={form.units_sold}
                onChange={(e) => setForm({ ...form, units_sold: e.target.value })}
                className="mt-1 h-9 text-sm"
                data-testid="sale-form-units-sold"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Units total (before sale)</Label>
              <Input
                type="number" step="0.0001" min="0"
                value={form.units_total}
                onChange={(e) => setForm({ ...form, units_total: e.target.value })}
                className="mt-1 h-9 text-sm"
                data-testid="sale-form-units-total"
              />
            </div>
          </>
        )}
        <div>
          <Label className="text-xs text-slate-600">Net proceeds</Label>
          <Input
            type="number" step="0.01" min="0" placeholder="0.00"
            value={form.proceeds_net}
            onChange={(e) => setForm({ ...form, proceeds_net: e.target.value })}
            className="mt-1 h-9 text-sm"
            data-testid="sale-form-proceeds"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Currency</Label>
          <Input
            type="text" maxLength={3}
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
            placeholder={defaultCurrency || 'USD'}
            className="mt-1 h-9 text-sm uppercase"
            data-testid="sale-form-currency"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Cost basis override <span className="text-slate-400">(optional)</span></Label>
          <Input
            type="number" step="0.01" min="0" placeholder={fullSaleOnly ? 'Auto: original cost (incl. capex if you set it)' : 'Auto: cost × fraction'}
            value={form.cost_basis_override}
            onChange={(e) => setForm({ ...form, cost_basis_override: e.target.value })}
            className="mt-1 h-9 text-sm"
            data-testid="sale-form-cost-basis-override"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600">Notes <span className="text-slate-400">(optional)</span></Label>
        <Input
          type="text" maxLength={500}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder='e.g., "Trimmed at all-time-high"'
          className="mt-1 h-9 text-sm"
          data-testid="sale-form-notes"
        />
      </div>
      {!editingId && !fullSaleOnly && (
        <div className="flex items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
          <Switch
            checked={form.close_holding}
            onCheckedChange={(v) => setForm({ ...form, close_holding: v })}
            data-testid="sale-form-close-holding"
          />
          <span className="text-xs text-slate-700">
            Mark this holding closed after the sale (hides it from your dashboard)
          </span>
        </div>
      )}
      {!editingId && fullSaleOnly && (
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="sale-form-full-disposal-note">
          This asset type is recorded as a full disposal — the holding will be closed automatically after the sale.
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving} data-testid="sale-form-cancel">
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button type="button" size="sm" onClick={onSave} disabled={saving}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="sale-form-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
          {editingId ? 'Save changes' : 'Record sale'}
        </Button>
      </div>
    </div>
  );
}
